import { useState, useEffect } from "react";
import { Table, Tooltip, message, Spin, Tag } from "antd";
import { CheckCircleFilled, MinusCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { orpcClient } from "@/lib/orpc-client";
import { apiUserGetMe } from "@/api";

type EmployeeWithRoles = {
  id: string;
  memberId: string;
  name: string;
  email: string;
  roles: string[];
  rawRoles: string[];
  orgRole: string;
};

type RoleQuota = {
  role: string;
  allowedCount: number;
  currentCount: number;
};

function PermissionOverview({ roleQuotas: roleQuotasProp }: { roleQuotas: RoleQuota[] }) {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeWithRoles[]>([]);
  const [currentUserRoles, setCurrentUserRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [roleQuotas, setRoleQuotas] = useState<RoleQuota[]>(roleQuotasProp);

  const ROLE_DISPLAY = [
    { id: "admin", name: t("employee.page.roles.admin.name") },
    { id: "project", name: t("employee.page.roles.project.name") },
    { id: "projectReport", name: t("employee.page.roles.projectReport.name") },
    { id: "purchase", name: t("employee.page.roles.purchase.name") },
    {
      id: "purchaseManager",
      name: t("employee.page.roles.purchaseManager.name"),
    },
    { id: "inventory", name: t("employee.page.roles.inventory.name") },
    { id: "finance", name: t("employee.page.roles.finance.name") },
  ];

  useEffect(() => {
    setRoleQuotas(roleQuotasProp);
  }, [roleQuotasProp]);

  const canEdit =
    (currentUserRoles ?? []).includes("owner") ||
    (currentUserRoles ?? []).includes("admin");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [result, me] = await Promise.all([
        orpcClient.employee.listRole({}),
        apiUserGetMe(),
      ]);
      const list = Array.isArray(result) ? result : [];
      setEmployees(
        list.map((item) => {
          const rawRoles = (item.role ?? "")
            .split(",")
            .filter((r: string) => r.trim());
          const allRoles = [...new Set(rawRoles.map((r: string) => r.trim()))];
          const orgRole = allRoles[0] ?? "";
          return {
            id: item.id,
            memberId: item.id,
            name: item.name,
            email: item.email,
            roles: allRoles,
            rawRoles,
            orgRole,
          };
        }),
      );
      const currentMember = list.find((u) => u.email === (me as { email: string })?.email);
      setCurrentUserRoles(
        currentMember
          ? [
              ...new Set<string>(
                (currentMember.role ?? "")
                  .split(",")
                  .map((r: string) => r.trim())
                  .filter(Boolean),
              ),
            ]
          : [],
      );
    } catch {
      message.error(t("employee.page.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (
    memberId: string,
    role: string,
    hasRole: boolean,
  ) => {
    if (!canEdit) return;
    const key = `${memberId}-${role}`;
    setToggling(key);
    try {
      if (hasRole) {
        const target = employees.find((emp) => emp.memberId === memberId);
        const storedRole =
          (target?.rawRoles ?? []).find((r) => r.trim() === role) ?? role;
        await orpcClient.employee.removeRole({
          employeeId: memberId,
          role: storedRole,
        });
        setEmployees((prev) =>
          prev.map((emp) =>
            emp.memberId === memberId
              ? {
                  ...emp,
                  roles: (emp.roles ?? []).filter((r) => r !== role),
                  rawRoles: (emp.rawRoles ?? []).filter(
                    (r) => r.trim() !== role,
                  ),
                }
              : emp,
          ),
        );
        setRoleQuotas((prev) =>
          prev.map((q) =>
            q.role === role ? { ...q, currentCount: Math.max(0, q.currentCount - 1) } : q,
          ),
        );
      } else {
        await orpcClient.employee.addRole({ employeeId: memberId, role });
        setEmployees((prev) =>
          prev.map((emp) =>
            emp.memberId === memberId
              ? {
                  ...emp,
                  roles: [...(emp.roles ?? []), role],
                  rawRoles: [...(emp.rawRoles ?? []), role],
                }
              : emp,
          ),
        );
        setRoleQuotas((prev) =>
          prev.map((q) =>
            q.role === role ? { ...q, currentCount: q.currentCount + 1 } : q,
          ),
        );
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error && err.message.includes("Seat quota")
          ? t("employee.page.seatQuotaFull")
          : err instanceof Error && err.message.includes("quota")
            ? t("employee.page.quotaFull")
            : t("employee.page.operationFailed");
      message.error(msg);
    } finally {
      setToggling(null);
    }
  };

  const columns = [
    {
      title: t("employee.page.permissionEmployeeColumn"),
      dataIndex: "name",
      key: "name",
      fixed: "left" as const,
      width: 180,
      render: (_: string, record: EmployeeWithRoles) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.name}</div>
          <div style={{ fontSize: 12, color: "#999" }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: t("employee.page.permissionRoleColumn"),
      key: "orgRole",
      width: 100,
      align: "center" as const,
      render: (_: unknown, record: EmployeeWithRoles) => {
        const orgRole = record.orgRole;
        if (orgRole === "owner") return <Tag color="gold">{t("employee.page.orgRoleOwner")}</Tag>;
        if (orgRole === "admin") return <Tag color="red">{t("employee.page.orgRoleAdmin")}</Tag>;
        return <Tag color="blue">{t("employee.page.orgRoleMember")}</Tag>;
      },
    },
    ...ROLE_DISPLAY.map((role) => {
      const quota = roleQuotas.find((q) => q.role === role.id);
      const isQuotaFull =
        quota !== undefined && quota.currentCount >= quota.allowedCount;
      return {
        title: (
          <div style={{ textAlign: "center" as const, fontSize: 12, fontWeight: 500 }}>
            <div>{role.name}</div>
            {quota && (() => {
              const available = quota.allowedCount - quota.currentCount;
              return (
                <Tag color={available > 10 ? "default" : available > 0 ? "warning" : "error"} style={{ marginTop: 4, fontSize: 11 }}>
                  {available > 0 ? `${available} ${t("employee.page.available")}` : t("employee.page.depleted")}
                </Tag>
              );
            })()}
          </div>
        ),
        key: role.id,
        width: 120,
        align: "center" as const,
        render: (_: unknown, record: EmployeeWithRoles) => {
          const isFullAccess =
            record.orgRole === "owner" ||
            record.orgRole === "admin" ||
            (record.roles ?? []).includes("admin");
          const hasRole = isFullAccess || (record.roles ?? []).includes(role.id);
          const key = `${record.memberId}-${role.id}`;
          const isToggling = toggling === key;
          const addDisabled = !hasRole && isQuotaFull;
          return (
            <Tooltip
              title={
                !canEdit
                  ? t("employee.page.needAdminPermission")
                  : addDisabled
                    ? t("employee.page.quotaFull")
                    : hasRole
                      ? t("employee.page.clickToRevoke")
                      : t("employee.page.clickToGrant")
              }
            >
              <button
                type="button"
                onClick={() =>
                  !addDisabled && handleToggle(record.memberId, role.id, hasRole)
                }
                disabled={!canEdit || isToggling || addDisabled}
                style={{
                  border: "none",
                  background: "none",
                  cursor: canEdit && !addDisabled ? "pointer" : "not-allowed",
                  opacity: 1,
                  padding: 4,
                  borderRadius: 4,
                }}
              >
                {isToggling ? (
                  <Spin size="small" />
                ) : hasRole ? (
                  <CheckCircleFilled style={{ fontSize: 22, color: "#1677ff" }} />
                ) : (
                  <MinusCircleOutlined style={{ fontSize: 22, color: "#d9d9d9" }} />
                )}
              </button>
            </Tooltip>
          );
        },
      };
    }),
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={employees}
      rowKey="id"
      pagination={false}
      scroll={{ x: "max-content" }}
      bordered
      size="middle"
    />
  );
}

export default PermissionOverview;
